import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: ('CONSULTANT' | 'TRAINER' | 'CLIENT')[];
  trainerId?: string;
  createdAt?: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  activeRole: string | null;
  isAuthenticated: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  updateUser: (user: User) => void;
  switchRole: (role: string) => void;
}

// Helper function to get highest role
const getHighestRole = (roles: string[]): string => {
  if (roles.includes('CONSULTANT')) return 'CONSULTANT';
  if (roles.includes('TRAINER')) return 'TRAINER';
  return 'CLIENT';
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      activeRole: null,
      isAuthenticated: false,
      login: (token: string, user: User) => {
        const highestRole = getHighestRole(user.roles);
        set({
          token,
          user,
          activeRole: highestRole,
          isAuthenticated: true,
        });
      },
      logout: () =>
        set({
          token: null,
          user: null,
          activeRole: null,
          isAuthenticated: false,
        }),
      updateUser: (user: User) =>
        set((state) => ({
          ...state,
          user,
          // Reset to highest role if current active role is no longer available
          activeRole: user.roles.includes(state.activeRole as any) 
            ? state.activeRole 
            : getHighestRole(user.roles),
        })),
      switchRole: (role: string) => {
        const state = get();
        if (state.user?.roles.includes(role as any)) {
          set({ activeRole: role });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        activeRole: state.activeRole,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
